/**
 * Crew pay data (features.crewPay). Crew invoices and pay rates are read under RLS; crew pay details on crew
 * records are edited under RLS by the office; pay rates and bank details by directors. Making, sending, paying and
 * exporting go through the API. Crew members see their own statements through /crew/pay.
 */
import { adminBlob, adminFetch, crewBlob, crewFetch } from "../../lib/api";
import { PREVIEW } from "../../lib/preview";
import { supabase } from "../../lib/supabase";

const previewOnly = (): never => {
  throw new Error("Not available in preview mode.");
};
const check = <T>(r: { data: T | null; error: { message: string; code?: string } | null }): T => {
  if (r.error) throw new Error(r.error.code === "42501" ? "Only a director can change this." : r.error.message);
  return r.data as T;
};
const num = (v: unknown) => Number(v ?? 0);

export type Status = "issued" | "confirmed" | "queried" | "paid" | "void";
export const STATUS_LABEL: Record<Status, string> = { issued: "Not confirmed", confirmed: "Confirmed", queried: "Queried", paid: "Paid", void: "Void" };
export interface Rates { day: number; night: number; weekend: number; publicHoliday: number; overtime: number }
export interface CrewInvoiceRow {
  id: string; number: string; crew_member_id: string; pay_month: string; issue_date: string; subtotal: number; gst: number; total: number; status: Status;
  is_valid: boolean; invalid_reasons: string[]; due_date: string | null; sent_at: string | null; confirmed_at: string | null; query_note: string | null;
  disputed_amount: number | null; dispute_reason: string | null; paid_at: string | null; paid_amount: number | null; paid_reference: string | null;
  snapshot: { title: string; crew: { name: string; email: string | null; abn: string | null; gstRegistered: boolean } };
}
export interface PayLine {
  docketCrewId: string; docketId: string; shiftDate: string; description: string; dayMinutes: number; nightMinutes: number; weekendMinutes: number; publicHolidayMinutes: number; overtimeMinutes: number; amount: number; clientPaid: boolean;
}
export interface PayMember {
  id: string; full_name: string; email: string | null; active: boolean; is_partner: boolean; abn: string | null; gst_registered: boolean;
  super_entity: "individual" | "company" | "trust" | null; public_liability_expiry: string | null;
}
export interface Preview { month: string; people: { crewMemberId: string; name: string; lines: number; minutes: number; amount: number; problems: string[]; partner: boolean }[] }

const toInvoice = (r: Record<string, unknown>): CrewInvoiceRow => ({
  ...(r as unknown as CrewInvoiceRow), subtotal: num(r.subtotal), gst: num(r.gst), total: num(r.total),
  disputed_amount: r.disputed_amount === null ? null : num(r.disputed_amount), paid_amount: r.paid_amount === null ? null : num(r.paid_amount),
});
const toRates = (r: Record<string, unknown>): Rates => ({ day: num(r.day), night: num(r.night), weekend: num(r.weekend), publicHoliday: num(r.public_holiday), overtime: num(r.overtime) });

export async function listCrewInvoices(month?: string): Promise<CrewInvoiceRow[]> {
  if (PREVIEW) return [];
  let q = supabase.from("crew_invoices").select("*").order("created_at", { ascending: false }).limit(500);
  if (month) q = q.eq("pay_month", `${month}-01`);
  return (check(await q) as Record<string, unknown>[]).map(toInvoice);
}

export const crewInvoice = (id: string) => (PREVIEW ? previewOnly() : adminFetch<{ invoice: Record<string, unknown>; lines: PayLine[] }>(`/crew-pay/${id}`));
export const previewMonth = (month: string) => (PREVIEW ? previewOnly() : adminFetch<Preview>(`/crew-pay/preview?month=${month}`));
export const makeMonth = (month: string) =>
  PREVIEW ? previewOnly() : adminFetch<{ made: { id: string; number: string; total: number; isValid: boolean }[]; skipped: { name: string; reason: string }[] }>("/crew-pay/make", { method: "POST", body: JSON.stringify({ month }) });
export const crewInvoicePdf = (id: string) => adminBlob(`/crew-pay/${id}/pdf`, {}, { timeoutMs: 60_000 });
export const sendCrewInvoice = (id: string) => adminFetch(`/crew-pay/${id}/send`, { method: "POST" }, { timeoutMs: 60_000 });
export const revalidate = (id: string) => adminFetch<{ invoice: { isValid: boolean; invalidReasons: string[]; dueDate: string | null } }>(`/crew-pay/${id}/revalidate`, { method: "POST" });
export const markPaid = (id: string, paidOn: string, amount: number, reference: string | null) => adminFetch(`/crew-pay/${id}/paid`, { method: "POST", body: JSON.stringify({ paidOn, amount, reference }) });
export const disputeAmount = (id: string, amount: number, reason: string) => adminFetch<{ emailed: boolean }>(`/crew-pay/${id}/dispute`, { method: "POST", body: JSON.stringify({ amount, reason }) });
export const voidCrewInvoice = (id: string, reason: string) => adminFetch(`/crew-pay/${id}/void`, { method: "POST", body: JSON.stringify({ reason }) });
export const paymentsFile = () => adminBlob("/crew-pay/export/payments");
export const superReport = (month: string) => adminBlob(`/crew-pay/export/super?month=${month}`);

/** Save a downloaded file (CSV) under its name. */
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* ---------- crew pay details, rates, bank details ---------- */

export async function payMembers(): Promise<PayMember[]> {
  if (PREVIEW) return [];
  return check(await supabase.from("crew_members").select("id, full_name, email, active, is_partner, abn, gst_registered, super_entity, public_liability_expiry").order("full_name")) as PayMember[];
}

export async function savePayDetails(id: string, p: Pick<PayMember, "is_partner" | "abn" | "gst_registered" | "super_entity"> & Partial<Pick<PayMember, "public_liability_expiry">>) {
  check(await supabase.from("crew_members").update(p).eq("id", id));
}

export async function payRates(): Promise<{ standard: Rates | null; byMember: Record<string, Rates> }> {
  if (PREVIEW) return { standard: null, byMember: {} };
  const rows = check(await supabase.from("pay_rates").select("*")) as Record<string, unknown>[];
  const std = rows.find((r) => !r.crew_member_id);
  return { standard: std ? toRates(std) : null, byMember: Object.fromEntries(rows.filter((r) => r.crew_member_id).map((r) => [r.crew_member_id as string, toRates(r)])) };
}

async function orgId(): Promise<{ org: string; uid: string }> {
  const { data: s } = await supabase.auth.getSession();
  const uid = s.session?.user.id ?? "";
  const { data, error } = await supabase.from("profiles").select("organisation_id, role").eq("id", uid).single();
  if (error) throw new Error(error.message);
  return { org: data.organisation_id as string, uid };
}

export async function myRole(): Promise<string | null> {
  if (PREVIEW) return "director";
  const { data: s } = await supabase.auth.getSession();
  const { data } = await supabase.from("profiles").select("role").eq("id", s.session?.user.id ?? "").maybeSingle();
  return (data?.role as string | undefined) ?? null;
}

/** Save the business default (crewMemberId null) or one crew member's own rates. */
export async function saveRates(crewMemberId: string | null, r: Rates) {
  const { org, uid } = await orgId();
  const row = { organisation_id: org, crew_member_id: crewMemberId, day: r.day, night: r.night, weekend: r.weekend, public_holiday: r.publicHoliday, overtime: r.overtime, updated_by: uid };
  let q = supabase.from("pay_rates").select("id").eq("organisation_id", org);
  q = crewMemberId ? q.eq("crew_member_id", crewMemberId) : q.is("crew_member_id", null);
  const existing = check(await q.maybeSingle()) as { id: string } | null;
  check(existing ? await supabase.from("pay_rates").update(row).eq("id", existing.id) : await supabase.from("pay_rates").insert(row));
}

export async function removeOwnRates(crewMemberId: string) {
  check(await supabase.from("pay_rates").delete().eq("crew_member_id", crewMemberId));
}

export interface Bank { account_name: string; bsb: string; account_number: string }
export async function banks(): Promise<Record<string, Bank>> {
  if (PREVIEW) return {};
  const rows = check(await supabase.from("crew_bank_details").select("crew_member_id, account_name, bsb, account_number")) as (Bank & { crew_member_id: string })[];
  return Object.fromEntries(rows.map((r) => [r.crew_member_id, r]));
}
export async function saveBank(crewMemberId: string, b: Bank) {
  const { org, uid } = await orgId();
  check(await supabase.from("crew_bank_details").upsert({ crew_member_id: crewMemberId, organisation_id: org, ...b, updated_by: uid }, { onConflict: "crew_member_id" }));
}

/* ---------- the crew member's own statements ---------- */

export interface MyStatement {
  id: string; number: string; month: string; total: number; gst: number; status: Status; isValid: boolean; invalidReasons: string[]; dueDate: string | null;
  paidAt: string | null; disputedAmount: number | null; disputeReason: string | null; queryNote: string | null;
}
export const myStatements = () => (PREVIEW ? Promise.resolve({ statements: [] as MyStatement[] }) : crewFetch<{ statements: MyStatement[] }>("/pay"));
export const myStatementPdf = (id: string) => crewBlob(`/pay/${id}/pdf`, { timeoutMs: 60_000 });
export const confirmStatement = (id: string) => crewFetch(`/pay/${id}/confirm`, { method: "POST" });
export const queryStatement = (id: string, note: string) => crewFetch(`/pay/${id}/query`, { method: "POST", body: JSON.stringify({ note }) });
